<template>
  <div
    v-if="activeIndex !== null"
    class="progress-gauge"
  >
    <div class="progress-gauge__bar">
      <!--
        Lines: 3 connectors between the 4 dots. Each line spans from the
        previous dot's center to the next dot's center; the dots (rendered
        after the lines in DOM order) cover the overlap, so the visible line
        always terminates at the adjacent dot's outer edge regardless of
        whether the dot is in past, active, or upcoming state.
      -->
      <div
        class="progress-gauge__line progress-gauge__line--first"
        :class="{ 'progress-gauge__line--primary': isConnectorPrimary(0) }"
      />
      <div
        class="progress-gauge__line progress-gauge__line--middle"
        :class="{ 'progress-gauge__line--primary': isConnectorPrimary(1) }"
      />
      <div
        class="progress-gauge__line progress-gauge__line--last"
        :class="{ 'progress-gauge__line--primary': isConnectorPrimary(2) }"
      />
      <!--
        Dots: first and last anchor to the bar's left/right edges so the gauge
        spans its full width; middle two center on 33.13% / 66.27% (Figma's
        frame proportions: 55, 110, 110, 57 out of 332).
      -->
      <div
        class="progress-gauge__dot progress-gauge__dot--first"
        :class="dotStateClass(1)"
      />
      <div
        class="progress-gauge__dot progress-gauge__dot--second"
        :class="dotStateClass(2)"
      />
      <div
        class="progress-gauge__dot progress-gauge__dot--third"
        :class="dotStateClass(3)"
      />
      <div
        class="progress-gauge__dot progress-gauge__dot--fourth"
        :class="dotStateClass(4)"
      />
    </div>
    <div class="progress-gauge__labels">
      <span
        class="progress-gauge__label progress-gauge__label--start"
        :class="{ 'progress-gauge__label--active': activeIndex === 1 }"
      >
        {{ labelPreparing }}
      </span>
      <span
        class="progress-gauge__label progress-gauge__label--center"
        :class="{ 'progress-gauge__label--active': activeIndex === 2 }"
      >
        {{ labelShipped }}
      </span>
      <span
        class="progress-gauge__label progress-gauge__label--center"
        :class="{ 'progress-gauge__label--active': activeIndex === 3 }"
      >
        {{ labelOutForDelivery }}
      </span>
      <span
        class="progress-gauge__label progress-gauge__label--end"
        :class="{ 'progress-gauge__label--active': activeIndex === 4 }"
      >
        {{ labelDelivered }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import { PortfolioTrackerDisplayStage } from '@/lib/portfolioTrackerDisplay'

// Maps the 6-value `PortfolioTrackerDisplayStage` onto the 4-dot gauge:
//   preparing       → dot 1 active
//   shipped         → dot 2 active
//   outForDelivery  → dot 3 active
//   delivered       → dot 4 active
//   billed          → gauge hidden (terminal — iteration 6 panel covers the state)
//   returned        → gauge hidden (terminal — iteration 6 hybrid panel covers it)
//
// The gauge stretches horizontally with its container. Dots and labels are
// positioned in CSS to match the Figma's frame proportions: first/last
// elements anchor to the bar edges; middle dots center on 33.13% / 66.27%;
// label frames take 16.57% / 33.13% / 33.13% / 17.17% of total width
// (55/110/110/57 of the Figma's 332-wide tracker).
export default defineComponent({
  name: 'ProgressGauge',
  props: {
    displayStage: {
      type: String as PropType<PortfolioTrackerDisplayStage>,
      required: true
    },
    labelPreparing: { type: String, required: true },
    labelShipped: { type: String, required: true },
    labelOutForDelivery: { type: String, required: true },
    labelDelivered: { type: String, required: true }
  },
  computed: {
    activeIndex(): number | null {
      switch (this.displayStage) {
        case PortfolioTrackerDisplayStage.Preparing:
          return 1
        case PortfolioTrackerDisplayStage.Shipped:
          return 2
        case PortfolioTrackerDisplayStage.OutForDelivery:
          return 3
        case PortfolioTrackerDisplayStage.Delivered:
          return 4
        case PortfolioTrackerDisplayStage.Billed:
        case PortfolioTrackerDisplayStage.Returned:
          return null
      }
      return null
    }
  },
  methods: {
    dotStateClass(dotIndex: number): string {
      if (this.activeIndex === null) return 'progress-gauge__dot--upcoming'
      if (dotIndex < this.activeIndex) return 'progress-gauge__dot--past'
      if (dotIndex === this.activeIndex) return 'progress-gauge__dot--in-progress'
      return 'progress-gauge__dot--upcoming'
    },
    isConnectorPrimary(connectorIndex: number): boolean {
      // Connector 0 sits between dots 1-2; it carries the primary color once
      // the user has reached or passed the dot to its right (i.e. dot N+1
      // where N = connectorIndex + 1).
      if (this.activeIndex === null) return false
      return this.activeIndex > connectorIndex + 1
    }
  }
})
</script>

<style scoped lang="scss">
.progress-gauge {
  // Tunable: dot/line sizes. Keep these in sync with the Figma spec
  // (inactive 8x8 = 0.5rem; active 12x12 = 0.75rem; connector 2px stroke).
  $inactive-dot: 0.5rem;
  $active-dot: 0.75rem;
  $line-thickness: 2px;
  $primary: #bb4a92;
  $secondary: #dddddd;

  &__bar {
    position: relative;
    height: $active-dot; // tall enough for the active dot
    margin-bottom: 0.5rem;
  }

  // Connectors. Each line spans from one dot's CENTER position to the
  // next dot's CENTER; the dots cover the overlap. `left: $inactive-dot / 2`
  // matches the first dot's center (which is 4px from the bar's left edge
  // since the first dot is anchored at left: 0 with width 0.5rem).
  &__line {
    position: absolute;
    top: calc(50% - #{$line-thickness} / 2);
    height: $line-thickness;
    background: $secondary;

    &--primary {
      background: $primary;
    }

    &--first {
      left: calc(#{$inactive-dot} / 2);
      right: calc(100% - 33.1325%);
    }

    &--middle {
      left: 33.1325%;
      right: calc(100% - 66.2651%);
    }

    &--last {
      left: 66.2651%;
      right: calc(#{$inactive-dot} / 2);
    }
  }

  &__dot {
    position: absolute;
    width: $inactive-dot;
    height: $inactive-dot;
    border-radius: 50%;
    top: calc(50% - #{$inactive-dot} / 2);

    &--past {
      background: $primary;
    }

    &--upcoming {
      background: $secondary;
    }

    // Active dot scales up to 0.75rem with a primary-color ring + white fill.
    // `top` recalculated so the dot's center stays at the bar's vertical
    // midpoint regardless of size.
    &--in-progress {
      width: $active-dot;
      height: $active-dot;
      top: calc(50% - #{$active-dot} / 2);
      background: #fff;
      border: $line-thickness solid $primary;
      box-sizing: border-box;
    }

    // Positions. First and last anchor to bar edges so the gauge spans full
    // width. Middle two center on their proportional positions (33.13% and
    // 66.27% from left). `transform: translateX(-50%)` keeps the dot centered
    // on its anchor x regardless of whether the dot is currently
    // 0.5rem (inactive) or 0.75rem (active).
    &--first {
      left: 0;
    }

    &--second {
      left: 33.1325%;
      transform: translateX(-50%);
    }

    &--third {
      left: 66.2651%;
      transform: translateX(-50%);
    }

    &--fourth {
      right: 0;
    }
  }

  &__labels {
    display: flex;
    align-items: flex-start;
    font-size: 0.75rem;
    color: #000;

    @include media-breakpoint-up(md) {
      font-size: 0.875rem;
    }
  }

  // Label frame widths mirror the Figma's 55 / 110 / 110 / 57 split out of
  // 332 total. Within each frame, text alignment positions the label per the
  // design: first start-aligns with the gauge's left edge; middle two center
  // on their dots; last end-aligns with the gauge's right edge.
  &__label {
    line-height: 1;

    &--start {
      flex: 0 0 16.5663%; // 55/332
      text-align: start;
    }

    &--center {
      flex: 0 0 33.1325%; // 110/332
      text-align: center;
    }

    &--end {
      flex: 0 0 17.1687%; // 57/332
      text-align: end;
    }

    &--active {
      font-weight: 700;
    }
  }
}
</style>
